import type { PassportSchemaDef } from "../types.js";

/**
 * EARLY-POSITIONING DRAFT — detergents are in the ESPR working plan 2025-2030
 * and already carry Detergents Regulation (EC) 648/2004 (ingredient data
 * sheet, surfactant biodegradability) + CLP hazard-labelling obligations that
 * the DPP consolidates. Reference template for the chemical-formulation family
 * (paint / lubricant / chemical share the identity + hazards + SDS shape).
 *
 * Field ids are STABLE; trued up when the delegated act lands by adding fields
 * / flipping `required`/`tier`, never by renaming ids.
 */
export const detergentV1: PassportSchemaDef = {
  id: "detergent-v1",
  version: "1.0.0-draft",
  title: {
    tr: "Deterjan Ürün Pasaportu",
    en: "Detergent Product Passport",
    de: "Wasch- und Reinigungsmittel-Produktpass",
  },
  jsonLdContext: {
    "@version": 1.1,
    "@vocab": "https://vocab.idpp.net/detergent/v1#",
    schema: "https://schema.org/",
    gs1: "https://gs1.org/voc/",
    identifier: "gs1:gtin",
    issuer: "schema:issuedBy",
    issuedAt: "schema:dateCreated",
  },
  categories: [
    {
      id: "identity",
      labels: { tr: "Ürün Kimliği", en: "Product Identity", de: "Produktidentität" },
      fields: [
        {
          id: "identity.productName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Ürün adı", en: "Product name", de: "Produktname" },
        },
        {
          id: "identity.productDescription",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ürün özeti", en: "Product summary", de: "Produktbeschreibung" },
          description: "Short description of the product and its intended use.",
          presentation: { kind: "summary" },
        },
        {
          id: "identity.gtin",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "GTIN", en: "GTIN", de: "GTIN" },
          description: "GS1 Global Trade Item Number (8/12/13/14 digits).",
        },
        {
          id: "identity.detergentType",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: [
            "laundry",
            "dishwashing-hand",
            "dishwashing-machine",
            "hard-surface-cleaner",
            "industrial",
            "other",
          ],
          labels: { tr: "Deterjan türü", en: "Detergent type", de: "Wasch-/Reinigungsmitteltyp" },
        },
        {
          id: "identity.physicalForm",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["liquid", "powder", "tablet", "capsule-pod", "gel", "other"],
          labels: { tr: "Fiziksel form", en: "Physical form", de: "Physikalische Form" },
        },
        {
          id: "identity.brand",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Marka", en: "Brand", de: "Marke" },
        },
        {
          id: "identity.manufacturerName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Üretici adı", en: "Manufacturer name", de: "Herstellername" },
        },
        {
          id: "identity.countryOfManufacture",
          type: "country",
          required: true,
          tier: "public",
          labels: { tr: "Üretim ülkesi", en: "Country of manufacture", de: "Herstellungsland" },
          description: "ISO 3166-1 alpha-2 code (e.g. TR).",
        },
        {
          id: "identity.netContent",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Net içerik", en: "Net content", de: "Nettoinhalt" },
          description: "E.g. 1.5 L, 2 kg, 30 washes.",
        },
        {
          id: "identity.batchNumber",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Parti numarası", en: "Batch number", de: "Chargennummer" },
        },
      ],
    },
    {
      id: "hazards",
      labels: {
        tr: "Tehlike Sınıflandırması (CLP)",
        en: "Hazard Classification (CLP)",
        de: "Gefahreneinstufung (CLP)",
      },
      fields: [
        {
          id: "hazards.clpSignalWord",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["danger", "warning", "none"],
          labels: { tr: "CLP uyarı kelimesi", en: "CLP signal word", de: "CLP-Signalwort" },
          highlight: true,
        },
        {
          id: "hazards.hazardStatements",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Tehlike ifadeleri (H)", en: "Hazard statements (H)", de: "Gefahrenhinweise (H)" },
          description: "H-codes, e.g. H319, H315.",
        },
        {
          id: "hazards.precautionaryStatements",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Önlem ifadeleri (P)",
            en: "Precautionary statements (P)",
            de: "Sicherheitshinweise (P)",
          },
        },
        {
          id: "hazards.ghsPictograms",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "GHS piktogramları", en: "GHS pictograms", de: "GHS-Piktogramme" },
          description: "E.g. GHS05, GHS07.",
        },
        {
          id: "hazards.skinIrritant",
          type: "boolean",
          required: false,
          tier: "public",
          labels: { tr: "Cilt tahriş edici", en: "Skin irritant", de: "Hautreizend" },
        },
      ],
    },
    {
      id: "ingredients",
      labels: { tr: "İçindekiler", en: "Ingredients", de: "Inhaltsstoffe" },
      fields: [
        {
          id: "ingredients.ingredientDisclosure",
          type: "string",
          required: true,
          tier: "public",
          labels: {
            tr: "İçindekiler beyanı",
            en: "Ingredient disclosure",
            de: "Angabe der Inhaltsstoffe",
          },
          description: "Ingredient data sheet summary per Detergents Regulation Annex VII.",
        },
        {
          id: "ingredients.ingredientList",
          type: "group",
          required: false,
          tier: "public",
          labels: { tr: "Bileşen listesi", en: "Ingredient list", de: "Inhaltsstoffliste" },
          description: "Structured ingredient breakdown (name / function / CAS / concentration).",
          itemFields: [
            {
              key: "name",
              type: "string",
              required: true,
              labels: { tr: "Ad", en: "Name", de: "Name" },
            },
            {
              key: "function",
              type: "string",
              required: false,
              labels: { tr: "İşlev", en: "Function", de: "Funktion" },
            },
            {
              key: "casNumber",
              type: "string",
              required: false,
              labels: { tr: "CAS numarası", en: "CAS number", de: "CAS-Nummer" },
            },
            {
              key: "concentrationRange",
              type: "string",
              required: false,
              labels: { tr: "Konsantrasyon aralığı", en: "Concentration range", de: "Konzentrationsbereich" },
            },
          ],
        },
        {
          id: "ingredients.fragranceAllergens",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Parfüm alerjenleri", en: "Fragrance allergens", de: "Duftstoff-Allergene" },
        },
        {
          id: "ingredients.preservatives",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Koruyucular", en: "Preservatives", de: "Konservierungsmittel" },
        },
        {
          id: "ingredients.enzymesPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: { tr: "Enzim içerir", en: "Contains enzymes", de: "Enthält Enzyme" },
        },
        {
          id: "ingredients.phosphatesPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: { tr: "Fosfat içerir", en: "Contains phosphates", de: "Enthält Phosphate" },
          highlight: true,
        },
        {
          id: "ingredients.phosphateContentPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "Fosfat içeriği", en: "Phosphate content", de: "Phosphatgehalt" },
        },
      ],
    },
    {
      id: "environment",
      labels: { tr: "Çevre", en: "Environment", de: "Umwelt" },
      fields: [
        {
          id: "environment.surfactantReadilyBiodegradable",
          type: "boolean",
          required: true,
          tier: "public",
          labels: {
            tr: "Yüzey aktif madde kolay biyobozunur",
            en: "Surfactants readily biodegradable",
            de: "Tenside leicht biologisch abbaubar",
          },
          description: "Required under Detergents Regulation (EC) 648/2004.",
          highlight: true,
        },
        {
          id: "environment.biodegradabilityPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "Biyobozunurluk oranı", en: "Biodegradability", de: "Biologische Abbaubarkeit" },
          highlight: true,
        },
        {
          id: "environment.aquaticToxicity",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["not-classified", "harmful", "toxic", "very-toxic"],
          labels: { tr: "Suda yaşayanlara toksisite", en: "Aquatic toxicity", de: "Aquatische Toxizität" },
        },
        {
          id: "environment.ecolabelType",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["eu-ecolabel", "nordic-swan", "other", "none"],
          labels: { tr: "Ekolojik etiket", en: "Ecolabel", de: "Umweltzeichen" },
        },
        {
          id: "environment.ecolabelLicenseNumber",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Ekolojik etiket lisans no",
            en: "Ecolabel licence number",
            de: "Umweltzeichen-Lizenznummer",
          },
        },
        {
          id: "environment.vocContentPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "VOC içeriği", en: "VOC content", de: "VOC-Gehalt" },
          highlight: true,
        },
      ],
    },
    {
      id: "usage",
      labels: { tr: "Kullanım ve Bertaraf", en: "Usage & Disposal", de: "Verwendung & Entsorgung" },
      fields: [
        {
          id: "usage.dosageInstructions",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Dozaj talimatları", en: "Dosage instructions", de: "Dosieranleitung" },
        },
        {
          id: "usage.recommendedDosage",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Önerilen doz", en: "Recommended dosage", de: "Empfohlene Dosierung" },
        },
        {
          id: "usage.concentrateFactor",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Konsantre faktörü", en: "Concentrate factor", de: "Konzentratfaktor" },
        },
        {
          id: "usage.disposalInstructions",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Bertaraf talimatları", en: "Disposal instructions", de: "Entsorgungshinweise" },
        },
        {
          id: "usage.sdsUrl",
          type: "url",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Güvenlik Bilgi Formu (SDS)",
            en: "Safety Data Sheet (SDS)",
            de: "Sicherheitsdatenblatt (SDB)",
          },
        },
        {
          id: "usage.safeUseUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: {
            tr: "Güvenli kullanım bağlantısı",
            en: "Safe use link",
            de: "Link zur sicheren Verwendung",
          },
        },
      ],
    },
  ],
};
